import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';
import { isCoreUserField, NewUser, type NewUserHistory, type OfficeLocation, type User, UserFlattened, UserHistory, userTypeEnum } from '@/server/db/schema';
import { type CoreMessage } from 'ai';
import timezone from 'dayjs/plugin/timezone';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ToolLog, type DataType, type SelectableValue, type DataSchema, type DataSchemaProperty } from '@/types';
import { cloneDeep } from 'lodash';
import type { BulkFilter } from '@/actions/types';
import {
  AGENT_CHAT_EVENT,
  USER_CHAT_EVENT,
  chatEventRole,
  isAssistantChatEvent,
  isUserChatEvent,
} from '@/lib/chatEvents';
import { useAppState } from './useAppState';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * The models a picker shows when the providers' own lists can't be loaded, and the display names
 * for ids we have named. The live list comes from `listModelOptions` in `src/lib/modelCatalog.ts`.
 *
 * The `value` is the bare model id that gets stored on a prompt row or action config, and it is
 * also what tells `resolveAgentModel` (src/lib/runAgentLoop.ts) which provider to send the call
 * to. OpenAI ids are stored plain, Claude ids are recognised by their `claude` prefix, and Groq
 * ids carry an explicit `groq/` prefix because Groq serves models named after other vendors.
 *
 * Adding a Claude id here has one extra consideration. Recent Claude models reject the sampling
 * parameters (`temperature` and friends) with a 400 rather than ignoring them, so
 * `acceptsSamplingParams` in `src/lib/aiModels.ts` decides whether the runners send them. A new
 * Claude entry is treated as rejecting them unless it is added to the allowlist in that file.
 */
export const COMMON_MODELS = [
  { value: 'gpt-5.1', label: 'GPT-5.1' },
  { value: 'gpt-5-mini', label: 'GPT-5 Mini' },
  { value: 'gpt-5-nano', label: 'GPT-5 Nano' },
  { value: 'gpt-4.1', label: 'GPT-4.1' },
  { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
  { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
  { value: 'claude-opus-5', label: 'Claude Opus 5' },
  { value: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
  { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
  { value: 'groq/llama-3.3-70b-versatile', label: 'Groq · Llama 3.3 70B' },
  { value: 'groq/llama-3.1-8b-instant', label: 'Groq · Llama 3.1 8B Instant' },
  { value: 'groq/openai/gpt-oss-120b', label: 'Groq · GPT-OSS 120B' },
  { value: 'groq/openai/gpt-oss-20b', label: 'Groq · GPT-OSS 20B' },
] as const;

export type ModelValue = typeof COMMON_MODELS[number]['value'];

export interface ModelOption {
  value: string;
  label: string;
}

/**
 * The options a model picker should show given the value it is bound to.
 *
 * A stored id may not be in `models` — agents seeded earlier hold the dated
 * `claude-haiku-4-5-20251001` rather than the alias. A `Select` matching none of its items renders
 * an empty trigger, so the unrecognised value is appended as its own option to keep it selected.
 */
export function modelOptionsFor(
  value?: string | null,
  models: readonly ModelOption[] = COMMON_MODELS
): ModelOption[] {
  const options: ModelOption[] = models.map((m) => ({
    value: m.value,
    label: m.label,
  }));
  if (value && !options.some((option) => option.value === value)) {
    options.push({ value, label: value });
  }
  return options;
}

type FormatDateReturnValue = { relative: string; iso: string, natural: string, verbose: string };
type FormatDateOpts = { timezone?: string }

export function formatDate(date?: Date | null | undefined, formatDateOpts: FormatDateOpts = {}): FormatDateReturnValue {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return {
      relative: "(not set)",
      iso: "(not set)",
      natural: "(not set)",
      verbose: "(not set)",
    };
  }
  const dt = dayjs.utc(date);
  const timezone = formatDateOpts.timezone || dayjs.tz.guess();
  try {
    return {
      relative: dt.fromNow(),
      iso: dt.toISOString(),
      natural: dayjs(date).tz(timezone).format('lll Z'),
      verbose: dayjs(date).tz(timezone).format('LLL Z'),
    };
  } catch (e) {
    console.error('Error formatting date:', e);
    return { relative: '(not set)', iso: '(not set)', natural: '(not set)', verbose: '(not set)' };
  }
}

export function parseChatStringtoHistoryEvents(text: string, workspaceId: number, agentId: number): NewUserHistory[] {
  const result: NewUserHistory[] = [];
  const lines: string[] = text.split('\n');
  let currentRole: "assistant" | "user" | null = null;
  let currentContent: string[] = [];

  for (const line of lines) {
    const roleMatch = line.match(/^\[(user|agent|assistant)(?:\s+.*?)?\]:?$/i);

    if (roleMatch) {
      if (currentRole) {
        const message = currentContent.join('\n').trim(); // Trim to remove any trailing newlines
        const historyEvent: NewUserHistory = {
          agentId,
          event: currentRole === "assistant" ? AGENT_CHAT_EVENT : USER_CHAT_EVENT,
          message,
          createdAt: new Date(),
        }
        result.push(historyEvent);
      }

      const matchedRole = roleMatch[1]?.toLowerCase();
      currentRole = matchedRole === "agent" ? "assistant" : (matchedRole as "assistant" | "user");
      currentContent = [];
    } else if (currentRole) {
      currentContent.push(line);
    }
  }

  if (currentRole) {
    const message = currentContent.join('\n');
    const historyEvent: NewUserHistory = {
      agentId,
      event: currentRole === "assistant" ? AGENT_CHAT_EVENT : USER_CHAT_EVENT,
      message,
      createdAt: new Date(),
    }
    result.push(historyEvent);
  }

  return result;
}


export function parseHistoryEventsToChatString(events: NewUserHistory[]) {
  // Matching on the literal phrases "user chat" and "agent chat" would drop any turn stored under
  // one of the alias names (user_message, assistant_message and friends), so a conversation created
  // through the Public API would come out of this formatter empty. The shared helpers recognise both
  // families of event name; see src/lib/chatEvents.ts for why both exist.
  const formattedMessages = events
    .filter(message => isUserChatEvent(message.event) || isAssistantChatEvent(message.event))
    .map((message) => {
      const sender = chatEventRole(message.event) === 'assistant' ? 'Agent' : 'User';
      return `[${sender}]\n${message.message || ""}`;
    })
    .join('\n\n');
  return formattedMessages;
}


export function parseChatEventsToChatString(events: CoreMessage[]): string {
  const formattedMessages = events?.map((message) => {
    const role = message.role === 'assistant' ? 'Agent' : 'User';
    const content = message.content;
    return `[${role}]\n${content}`;
  }).join('\n\n');
  return formattedMessages;
}

export function parseChatStringtoChatEvents(text: string): CoreMessage[] {
  const result: CoreMessage[] = [];
  const lines: string[] = text.split('\n');
  let currentRole: "assistant" | "user" | null = null;
  let currentContent: string[] = [];

  for (const line of lines) {
    const roleMatch = line.match(/^\[(user|agent|assistant)(?:\s+.*?)?\]:?$/i);

    if (roleMatch) {
      if (currentRole) {
        result.push({
          role: currentRole,
          content: currentContent.join('\n').trim()
        });
      }

      const matchedRole = roleMatch[1]?.toLowerCase();
      currentRole = matchedRole === "agent" ? "assistant" : (matchedRole as "assistant" | "user");
      currentContent = [];
    } else if (currentRole) {
      if (line.trim() !== '') {
        currentContent.push(line);
      }
    }
  }

  if (currentRole) {
    result.push({
      role: currentRole,
      content: currentContent.join('\n').trim()
    });
  }

  return result;
}

export function validateEnodable(params: any) {
  for (const key in params) {
    if (typeof params[key] === 'string' && !/^[\x20-\x7E]*$/.test(params[key])) {
      console.error(`Invalid character found in ${key}: ${params[key]}`);
      return false;
    }
  }
  return true;
}

// url of our site (accounting for prod, vercel staging env, localhost)
export const getSiteUrl = () => {
  let url =
    process?.env?.NEXT_PUBLIC_SITE_URL ?? // Manually set to prod url in .env
    process?.env?.NEXT_PUBLIC_VERCEL_URL ?? // Automatically set by Vercel.
    (typeof window !== 'undefined' ? window.location.origin : null) ?? // Client: use actual origin (fixes prod when env not set at build)
    'http://localhost:3012/' // Matches PORT in .env; embed snippet and other URLs use this
  url = url.startsWith('http') ? url : `https://${url}`
  url = url.endsWith('/') ? url : `${url}/`
  return url
}

/**
 * Check if a referer URL's hostname matches any of the allowed domain patterns.
 * Supports wildcard subdomains: "*.example.com" matches "sub.example.com" and "example.com".
 * Patterns may be saved with a scheme, port or path ("http://localhost:3000/"); only the hostname is compared.
 */
export function domainIsAllowed(referer: string, allowedDomains: string[]): boolean {
	try {
		const hostname = new URL(referer).hostname;
		return allowedDomains.some(rawPattern => {
			const pattern = patternHostname(rawPattern);
			if (pattern.startsWith('*.')) {
				const base = pattern.slice(2);
				return hostname === base || hostname.endsWith('.' + base);
			}
			return hostname === pattern;
		});
	} catch {
		return false;
	}
}

function patternHostname(pattern: string): string {
	const withoutScheme = pattern.trim().toLowerCase().replace(/^[a-z]+:\/\//, '');
	return withoutScheme.split(/[/:]/, 1)[0] ?? '';
}

export function emailIsOnWhitelist(email: string, whitelistRegexCsv: string): boolean {
  const whitelistPatterns = whitelistRegexCsv.split(',').map(pattern =>
    pattern.trim().replace(/\*/g, '.*').replace(/\?/g, '.')
  );

  for (const pattern of whitelistPatterns) {
    const regex = new RegExp(`^${pattern}$`, 'i');
    if (regex.test(email)) {
      return true;
    }
  }

  return false;
}

export function emailApproved(email: string): boolean {
  const whitelist = process.env.WHITELIST_ADMIN_EMAILS;
  if (!whitelist) {
    console.error("WHITELIST_ADMIN_EMAILS env var is not set, no user can sign in.")
    return false;
  }
  return emailIsOnWhitelist(email, whitelist);
}

export function getPostgresTypeForField(xFieldType: string | null | undefined): string {
  if (!xFieldType) {
    return ''; // No casting for unknown types
  }

  switch (xFieldType) {
    case 'boolean':
    case 'checkbox':
      return '::boolean';
    case 'number':
      return '::numeric';
    case 'date':
      return '::timestamp';
    case 'text':
    case 'select':
    default:
      return ''; // No casting needed for text types
  }
}

export async function handleTranslate(content: string): Promise<string> {
  try {
    const state = useAppState.getState();
    const { currentWorkspace, currentAgent } = state;
    if (!currentWorkspace || !currentAgent) {
      throw new Error('No workspace/agent context');
    }
    const response = await fetch(`/api/${currentWorkspace.slug}/${currentAgent.slug}/llm/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content }),
    });

    if (!response.ok) {
      throw new Error('Translation failed');
    }

    const data = await response.json();
    return data.translation;
  } catch (error) {
    console.error('Error translating:', error);
    return 'Translation failed. Please try again.';
  }
}

export function formDataToObject(formData: FormData): Record<string, string> {
  const obj: Record<string, string> = {};
  for (const [key, value] of formData.entries()) {
    obj[key] = value.toString();
  }
  return obj;
}

export function generateJsonSchema({
  columnName,
  selectedType,
  description,
  selectableValues,
  aiModel,
  aiPrompt,
  renderType
}: {
  columnName: string;
  selectedType: DataType | null;
  description: string;
  selectableValues: SelectableValue[];
  aiModel?: string;
  aiPrompt?: string;
  renderType?: string;
}): Partial<DataSchema> {
  const propertyKey = columnName.toLowerCase().replace(/\s+/g, '_');
  const baseSchema: Partial<DataSchema> = {
    properties: {},
    required: []
  };

  const properties: Record<string, DataSchemaProperty> = {
    [propertyKey]: {
      type: selectedType === 'select' || selectedType === 'systemUserSelect' ? 'string' : selectedType === 'number' ? 'number' : selectedType === 'boolean' ? 'boolean' : 'string',
      title: columnName,
      description,
      'x-field-type': selectedType
    }
  };

  if (selectedType === 'select' && selectableValues.length > 0) {
    properties[propertyKey] = {
      ...properties[propertyKey],
      enum: selectableValues.map(v => v.value)
    } as any;
  }

  // systemUserSelect fields get auto-populated options from agent members
  if (selectedType === 'systemUserSelect') {
    properties[propertyKey] = {
      ...properties[propertyKey],
      'x-render-type': 'systemUser',
      'x-icon-name': 'User'
    } as any;
  }

  if (selectedType === 'ai prompt') {
    properties[propertyKey] = {
      ...properties[propertyKey],
      'x-ai-model': aiModel,
      'x-ai-prompt': aiPrompt
    } as any;
  }

  if (selectedType === 'date') {
    properties[propertyKey] = {
      ...properties[propertyKey],
      format: 'date-time',
      ...(renderType && { 'x-render-type': renderType })
    } as any;
  }

  if (selectedType === 'boolean') {
    properties[propertyKey] = {
      ...properties[propertyKey],
      type: 'boolean'
    } as any;
  }

  baseSchema.properties = properties;
  return baseSchema;
}



export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

// Used for Supabase Realtime payloads, which use snake_case column names
export function snakeToCamelKeys<T extends Record<string, any>>(obj: T): T {
  const result: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const camelKey = snakeToCamel(key);
      result[camelKey] = obj[key];
    }
  }
  return result;
}

// Structural equality with no type coercion, except Date vs ISO string, which compare by instant.
export function isDeepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a instanceof Date || b instanceof Date) {
    const ta = a instanceof Date ? a.getTime() : typeof a === 'string' ? new Date(a).getTime() : NaN;
    const tb = b instanceof Date ? b.getTime() : typeof b === 'string' ? new Date(b).getTime() : NaN;
    return !isNaN(ta) && ta === tb;
  }
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => isDeepEqual(v, b[i]));
  }
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => isDeepEqual((a as any)[k], (b as any)[k]));
}

// Table columns take precedence over JSONB data fields, to prevent stale/duplicate data
// in JSONB from overwriting actual column values.
export function flattenUser(user: User): User {
  return { ...(user.data || {}), ...user } as User;
}

// Splits a flat object into core fields and .data for DB update.
export function unflattenUser(user: any): Partial<User> {
  const core: any = {};
  const customData: any = {};
  for (const key in user) {
    // Skip 'data' and 'meta' - custom fields are already flattened to top level
    if (key === 'data' || key === 'meta') continue;
    if (isCoreUserField(key)) {
      core[key] = user[key];
    } else {
      customData[key] = user[key];
    }
  }
  if (Object.keys(customData).length) core.data = customData;
  return core;
}

// Handles accented characters by normalizing them to their base form (e.g., León → leon)
export function generateSlug(name: string): string {
  return name
    .normalize('NFD') // Decompose accented characters into base + diacritical marks
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove non-alphanumeric characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading and trailing hyphens
}

// Fallback type detection based on value structure, used when schema info is missing or incorrect.
export function detectValueType(value: any): string {
  if (typeof value === 'boolean') {
    return '::boolean';
  }

  if (typeof value === 'number') {
    return '::numeric';
  }

  if (typeof value === 'string') {
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
    const dateRegex = /^\d{4}-\d{2}-\d{2}( \d{2}:\d{2}:\d{2})?$/;

    if (isoDateRegex.test(value) || dateRegex.test(value)) {
      return '::timestamp';
    }

    if (/^-?\d+(\.\d+)?$/.test(value)) {
      return '::numeric';
    }

    if (['true', 'false'].includes(value.toLowerCase())) {
      return '::boolean';
    }
  }

  return '';
}

export function getPostgresTypeForFieldWithFallback(
  xFieldType: string | null | undefined,
  value?: any
): string {
  const schemaType = getPostgresTypeForField(xFieldType);
  if (schemaType) {
    return schemaType;
  }

  if (value !== undefined) {
    return detectValueType(value);
  }

  return '';
}
/**
 * Normalize optional bulk filters so downstream callers always receive a strict
 * `'and' | 'or'` logical operator. UI callers frequently send `null` to mean
 * "use the default"; centralizing this helper keeps API routes consistent.
 */

export function normalizeBulkFilters(filters?: BulkFilter): BulkFilter | undefined {
  if (!filters) return undefined;
  return {
    filters: filters.filters,
    logicalOperator: filters.logicalOperator === 'or' ? 'or' : 'and'
  };
}
